import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { CheckboxGroup } from '@sm360/ui-library';
import { LocaleService } from '@sm360/widget-commons-core-services';
import moment from 'moment';
import { isEmpty } from 'lodash';

const MORNING = 'morning';
const AFTERNOON = 'afternoon';
const EVENING = 'evening';

const TimePicker = ({ min = 9, max = 19, steps = 2, forDate = 1649257740000, onTimePick, defaultTimes = [] }) => {
    const initialMount = useRef(true);

    const getTimes = (timeMin, timeMax) => {
        const times = [];
        const step = steps ? 60 / steps : 1;

        for (let t = timeMin; t < timeMax; t += 1) {
            const hour = String(t).padStart(2, 0);
            for (let s = 0; s < steps; s += 1) {
                const minutes = String(s * step).padStart(2, 0);
                times.push(`${hour}:${minutes}`);
            }
        }

        return times;
    };

    const timeBanks = {
        [MORNING]: min < 13 ? getTimes(min, 13) : [],
        [AFTERNOON]: max > 13 ? getTimes(13, max > 18 ? 18 : max) : [],
        [EVENING]: max > 18 ? getTimes(18, max) : [],
    };

    const initialTimes = useMemo(
        () => ({
            [MORNING]: defaultTimes.filter((time) => timeBanks[MORNING].includes(time)),
            [AFTERNOON]: defaultTimes.filter((time) => timeBanks[AFTERNOON].includes(time)),
            [EVENING]: defaultTimes.filter((time) => timeBanks[EVENING].includes(time)),
        }),
        [defaultTimes]
    );

    const [selectedTimes, setSelectedTimes] = useState(initialTimes);

    const date = moment(forDate);
    const currentDateTime = moment();

    // Reset to initial times if the date changes
    useEffect(
        () => {
            setSelectedTimes(initialTimes);
        },
        [forDate]
    );

    const getTimeOptions = (timeBank) =>
        timeBanks[timeBank].map((time) => {
            const formattedTimeStamp = `${date.format('YYYY-MM-DD')} ${time}`;
            const timeOptionDateTime = moment(formattedTimeStamp);
            const disabled = timeOptionDateTime.unix() < currentDateTime.unix();
            const checked = !disabled && selectedTimes[timeBank].includes(time);

            return {
                id: time,
                label: time,
                value: time,
                disabled,
                checked,
            };
        });

    const morningTimeOptions = getTimeOptions(MORNING);
    const afternoonTimeOptions = getTimeOptions(AFTERNOON);
    const eveningTimeOptions = getTimeOptions(EVENING);

    const onChanged = (selected, timeBank) => {
        setSelectedTimes({
            ...selectedTimes,
            [timeBank]: selected,
        });
    };

    useEffect(
        () => {
            if (initialMount.current) initialMount.current = false;
            else if (onTimePick) {
                onTimePick([...selectedTimes[MORNING], ...selectedTimes[AFTERNOON], ...selectedTimes[EVENING]]);
            }
        },
        [selectedTimes]
    );

    return (
        <div>
            {!isEmpty(timeBanks[MORNING]) && (
                <CheckboxGroup
                    displayAsButton
                    inline
                    label={LocaleService.t('TRADE_IN.SECTIONS_CONTENT.APPOINTMENT_FORM.MORNING')}
                    name="MorningheckboxGroup"
                    checked={selectedTimes[MORNING]}
                    onChange={(selected) => onChanged(selected, MORNING)}
                    onError={() => {}}
                    options={morningTimeOptions}
                    useGroupControl={false}
                />
            )}
            {!isEmpty(timeBanks[AFTERNOON]) && (
                <CheckboxGroup
                    displayAsButton
                    inline
                    checked={selectedTimes[AFTERNOON]}
                    label={LocaleService.t('TRADE_IN.SECTIONS_CONTENT.APPOINTMENT_FORM.AFTERNOON')}
                    name="AfternoonCheckboxGroup"
                    onChange={(selected) => onChanged(selected, AFTERNOON)}
                    onError={() => {}}
                    options={afternoonTimeOptions}
                    useGroupControl={false}
                />
            )}
            {!isEmpty(timeBanks[EVENING]) && (
                <CheckboxGroup
                    displayAsButton
                    inline
                    checked={selectedTimes[EVENING]}
                    label={LocaleService.t('TRADE_IN.SECTIONS_CONTENT.APPOINTMENT_FORM.EVENING')}
                    name="EveningCheckboxGroup"
                    onChange={(selected) => onChanged(selected, EVENING)}
                    onError={() => {}}
                    options={eveningTimeOptions}
                    useGroupControl={false}
                />
            )}
        </div>
    );
};

TimePicker.displayName = 'TimePicker';

TimePicker.propTypes = {
    min: PropTypes.number,
    max: PropTypes.number,
    steps: PropTypes.oneOf([1, 2, 4, 6, 12]),
    forDate: PropTypes.number, // unix timestamp including milliseconds
    onTimePick: PropTypes.func,
    defaultTimes: PropTypes.array,
};

export default TimePicker;
